import React, { useState, useEffect, useRef } from 'react';
import { ContentPopup } from 'components/ContentPopup.jsx';
import SettingsUserUnitPriceAttributeList from 'components/quote_builder/SettingsUserUnitPriceAttributeList.jsx';
import { machineSettingsConfig, machineTabConfig, machineCount, setMachineCount, refreshCountsAndResolve } from 'components/frank/utils/machineSettings.js';

// The /mcc filter-island control for the owner's OWN machines of ONE type, shown only on "My Manufacturing Lines".
// It opens the SAME settings list the quote-builder settings page uses (quote_builder/
// SettingsUserUnitPriceAttributeList) in a popup, so the owner can add / edit / remove their own machines
// (a UserUnitPriceAttribute of `machineKey` -- 'printing_machine' or 'die_cutting_machine') without leaving the
// calculator; the CRUD endpoints are all current_user-scoped and already exist. Parameterized by machineKey +
// the two label keys so ONE button serves every machine row.
//
// The LABEL reflects state: with no machines yet it invites "+ Add ..."; once there are some it is a plain
// "Manage" (gear).
//
// RE-SOLVE ON CLOSE, NOT PER SAVE. Each add/edit/remove the list makes dispatches quote_builder.user_unit_price_
// saved / _deleted on `document`; this button only NOTES that a change happened, and when the manager popup
// CLOSES it tells FrankQuoteBuilderPage once (frank:own-machines-changed) to re-solve the sections. A per-save
// re-solve would remount the subtree that holds this open popup and tear it down between machines, so the owner
// could not add several in a row (the library picker's whole point).
//
// suppressUrlSync keeps the embedded list from pushing /quote-builder/settings/... URLs into /mcc's history.
const XHR = { 'X-Requested-With': 'XMLHttpRequest' };

const ManageOwnMachinesButton = ({ machineKey, addLabelKey, manageLabelKey, allowedLibraryTypes }) => {
  const [open, setOpen] = useState(false);
  // Seeded from the count frank/edit.html.erb rendered at page load, so the label is right on first paint.
  const [count, setCount] = useState(machineCount(machineKey));
  // Set when a machine is added/edited/removed while the popup is open, so closing it re-solves the sections.
  const changedRef = useRef(false);

  // Re-read the owner's own count for this machine type and keep the window global fresh (so the label -- and a
  // later mount's seed -- are honest). Called on mount AND on every add/delete while the popup is open, since the
  // re-solve (and the remount that would otherwise refresh this) is now deferred to close.
  const refreshCount = () => {
    fetch('/quote_builder_user_unit_price_counts', { cache: 'no-store', headers: XHR })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (!data || !data.attribute_counts) { return; }
        const next = data.attribute_counts[machineKey] || 0;
        setMachineCount(machineKey, next);
        setCount(next);
      })
      .catch(() => {});
  };

  // Re-solve the sections once, for a change that has not already been handled by closePopup. Route it through
  // refreshCountsAndResolve rather than a bare event so the owner's OWN counts -- including the per-type maps the
  // type-aware setup gate reads (frank_<key>_count_by_type) -- are pulled fresh BEFORE the re-solve. A machine
  // added or removed here would otherwise leave those maps stale (this button's live label refresh only touches
  // the flat count), so the gate could still read a just-deleted machine as present. refreshCountsAndResolve ends
  // by announcing frank:own-machines-changed itself, which is what drives the re-solve. Only on CLOSE, never per
  // save -- a re-solve mid-edit would remount this subtree and tear the open popup down.
  const resolveIfChanged = () => {
    if (changedRef.current) {
      changedRef.current = false;
      refreshCountsAndResolve();
    }
  };

  useEffect(() => { refreshCount(); }, []);

  // Note any own-machine change made in the popup (the settings list dispatches these on document) and refresh
  // the count; the re-solve is deferred to closePopup so the popup survives adding several in a row.
  useEffect(() => {
    const onChanged = () => { changedRef.current = true; refreshCount(); };
    document.addEventListener('quote_builder.user_unit_price_saved', onChanged);
    document.addEventListener('quote_builder.user_unit_price_deleted', onChanged);
    return () => {
      document.removeEventListener('quote_builder.user_unit_price_saved', onChanged);
      document.removeEventListener('quote_builder.user_unit_price_deleted', onChanged);
    };
  }, []);

  // Safety net for finding it never closed: if this subtree is torn down mid-edit by SOME OTHER remount (not the
  // close path), still re-solve for whatever changed, so the page never keeps answers computed against the old
  // catalogue. Harmless after a normal close, which already reset changedRef.
  useEffect(() => resolveIfChanged, []);

  // Close the manager. If anything changed while it was open, re-solve the sections ONCE now (FrankQuoteBuilderPage
  // listens for this) -- the section remount that re-solve triggers is what would have closed the popup mid-edit.
  const closePopup = () => {
    setOpen(false);
    resolveIfChanged();
  };

  const tabConfig = machineTabConfig(machineKey);
  // Without the settings config the popup has nothing to render; do not offer a button that opens an empty modal.
  if (!tabConfig) { return null; }

  const hasNone = count === 0;

  return (
    <>
      <button
        type="button"
        className="dct-button dct-button--secondary dct-button--small"
        onClick={() => setOpen(true)}
        data-manage-machines={machineKey}
      >
        {hasNone ? (
          <><i className="fa fa-plus" aria-hidden="true" /> {t(addLabelKey)}</>
        ) : (
          <><i className="fa fa-cog" aria-hidden="true" /> {t(manageLabelKey)}</>
        )}
      </button>
      {open && (
        <ContentPopup
          autoOpen
          beforeClose={closePopup}
          modalStyle={{ width: '90%', maxWidth: '1000px' }}
          content={(
            <SettingsUserUnitPriceAttributeList
              userUnitPriceKey={machineKey}
              tabConfig={tabConfig}
              sectionAttributes={machineSettingsConfig(machineKey).attributes}
              suppressUrlSync
              // "+ Add" (no machines yet) opens STRAIGHT onto the add form / library picker -- no landing on an
              // empty listing and a second "New" click. "Manage" (has machines) opens the listing, so the owner
              // can edit / remove as well as add.
              autoStartNew={hasNone}
              // Constrain the library picker to the section's usable types, exactly as the inline empty-state
              // picker does -- the owner can add a machine from EITHER path, so both must offer the same set.
              allowedLibraryTypes={allowedLibraryTypes}
            />
          )}
        />
      )}
    </>
  );
};

export default ManageOwnMachinesButton;
